import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Star, RefreshCw, TrendingUp, Euro, Users, Zap, Gift, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PALIERS = [
  { min: 0,  pct: 0,  label: 'Nouveau',  color: 'var(--admin-teal)' },
  { min: 2,  pct: 5,  label: '🥉 Bronze', color: '#CD7F32' },
  { min: 5,  pct: 10, label: '🥈 Silver', color: '#9CA3AF' },
  { min: 10, pct: 15, label: '🏆 Gold',   color: 'var(--admin-gold)' },
  { min: 20, pct: 20, label: '⭐⭐⭐ Platine', color: '#E5E7EB' },
];

export default function AdminFidelite() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('clients');

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        fetch(`${BACKEND}/api/admin/fidelite/stats`, { headers: H }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/fidelite/clients?limit=50`, { headers: H }).then(r => r.json()),
      ]);
      setStats(s);
      setClients(c.clients || []);
    } catch { toast.error('Erreur'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  async function syncFromQuotes() {
    setSyncing(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/fidelite/sync-quotes`, { method: 'POST', headers: H });
      const data = await res.json();
      toast.success(data.message);
      load();
    } catch { toast.error('Erreur synchronisation'); }
    setSyncing(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="admin-fidelite">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Clients</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Fidelite & Cashback</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Programme de fidelisation</p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="adm-title"  style={{ color: 'var(--admin-gold)' }}>Fidélité & Cashback</h1>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Programme de fidélisation automatique</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
          <Button onClick={syncFromQuotes} disabled={syncing} size="sm"
            style={{ background: 'rgba(212,175,55,0.15)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <Zap size={14} className="mr-1" /> {syncing ? 'Sync...' : 'Sync depuis devis'}
          </Button>
        </div>
      </div>

      {/* Paliers */}
      <div className="p-5 rounded-2xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
        <h2 className="font-bold text-sm mb-4" style={{ color: 'var(--admin-text)' }}>Paliers du programme</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {PALIERS.map((p, i) => (
            <div key={i} className="p-3 rounded-xl text-center" style={{ background: `${p.color}10`, border: `1px solid ${p.color}25` }}>
              <p className="text-lg font-bold" style={{ color: p.color }}>{p.pct > 0 ? `-${p.pct}%` : 'Standard'}</p>
              <p className="text-xs font-semibold" style={{ color: p.color }}>{p.label}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--admin-faint)' }}>Dès {p.min} loc.</p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 rounded-xl" style={{ background: 'rgba(31,157,122,0.08)', border: '1px solid rgba(31,157,122,0.2)' }}>
          <p className="text-xs font-semibold" style={{ color: 'var(--admin-green)' }}>💰 Cashback automatique : 5% du montant total de chaque location</p>
          <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Crédité après paiement total. Utilisable sur la prochaine réservation.</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <Users size={18}/>, color: 'var(--admin-teal)', label: 'Clients fidèles', value: stats.total_clients },
            { icon: <Euro size={18}/>, color: 'var(--admin-gold)', label: 'CA total fidèles', value: `${stats.ca_total_fideles?.toFixed(0)||0}€` },
            { icon: <Gift size={18}/>, color: 'var(--admin-green)', label: 'Cashback distribué', value: `${stats.cashback_total_distribue?.toFixed(0)||0}€` },
            { icon: <Star size={18}/>, color: 'var(--admin-amber)', label: 'Cashback disponible', value: `${stats.cashback_en_attente?.toFixed(0)||0}€` },
          ].map((s, i) => (
            <div key={i} className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: `1px solid ${s.color}20` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: `${s.color}15`, color: s.color }}>{s.icon}</div>
              <p className="adm-title"  style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {['clients', 'paliers'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className="px-4 py-2 rounded-xl text-sm font-medium"
            style={activeTab === tab ? { background: 'rgba(212,175,55,0.15)', color: 'var(--admin-gold)' } : { color: 'var(--admin-muted)' }}>
            {tab === 'clients' ? 'Clients' : 'Répartition paliers'}
          </button>
        ))}
      </div>

      {activeTab === 'clients' && (
        loading ? (
          <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-16 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
        ) : (
          <div className="space-y-2">
            {clients.length === 0 ? (
              <div className="text-center py-12" style={{ color: 'var(--admin-faint)' }}>
                <Star size={40} className="mx-auto mb-3 opacity-20" />
                <p>Aucun client fidèle pour le moment</p>
                <p className="text-xs mt-1">Synchronisez depuis les devis pour initialiser.</p>
              </div>
            ) : clients.map(c => {
              const palier = c.palier_info || {};
              return (
                <div key={c.id || c.email} className="flex items-center gap-4 p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>{c.nom || c.email}</p>
                    <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{c.email}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-bold" style={{ color: palier.couleur || '#9CA3AF' }}>{palier.label}</p>
                    <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{c.nb_locations} location{c.nb_locations > 1 ? 's' : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold" style={{ color: 'var(--admin-green)' }}>{c.cashback_disponible?.toFixed(2)||'0.00'}€</p>
                    <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>cashback dispo</p>
                  </div>
                  {c.locations_restantes > 0 && (
                    <div className="text-right">
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{c.locations_restantes} loc. avant</p>
                      <p className="text-xs font-bold" style={{ color: c.prochain_palier?.couleur || '#D4AF37' }}>{c.prochain_palier?.label}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {activeTab === 'paliers' && stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(stats.paliers_repartition || {}).map(([label, count]) => (
            <div key={label} className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <p className="adm-title"  style={{ color: 'var(--admin-gold)' }}>{count}</p>
              <p className="text-sm" style={{ color: 'var(--admin-text)' }}>{label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
